import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { categoryStyle, eventLabel, indexEvents } from './events';
import { ActivityEventGroups } from './types';

const useStyles = makeStyles({
  paper: { borderRadius: 16, width: 680 },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  search: { marginBottom: 16, '& .MuiOutlinedInput-root': { borderRadius: 24 } },
  selected: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: 12,
    maxHeight: 130,
    overflow: 'auto',
  },
  chip: { background: '#F0E9FF', border: '1px solid #DCCFFB' },
  group: { padding: '12px 0' },
  groupTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: '#5C5C70',
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 44,
    paddingRight: 8,
    borderRadius: 8,
    cursor: 'pointer',
    '&:hover': { background: '#F8F6FF' },
  },
  name: { flex: 1, fontSize: 14 },
  key: {
    fontFamily: 'monospace',
    fontSize: 11,
    color: '#5C5C70',
    overflowWrap: 'anywhere',
    maxWidth: '50%',
  },
  footer: {
    padding: '16px 24px',
    borderTop: '1px solid #EEEEF3',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  button: { borderRadius: 24, padding: '8px 24px' },
});

interface Props {
  events: ActivityEventGroups;
  selected: string[];
  onApply: (events: string[]) => void;
  onClose: () => void;
}

const ActivityEventFilterDialog = ({ events, selected, onApply, onClose }: Props) => {
  const classes = useStyles();
  const [draft, setDraft] = useState(selected);
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>();
  const optionsRef = useRef<HTMLDivElement>(null);
  const dictionary = useMemo(() => indexEvents(events), [events]);
  const matches = useMemo(
    () =>
      Object.keys(dictionary).filter((event) =>
        `${eventLabel(event, dictionary)} ${event}`
          .toLowerCase()
          .includes(search.toLowerCase().trim())
      ),
    [dictionary, search]
  );
  const groups = useMemo(
    () =>
      matches.reduce((result: Record<string, string[]>, event) => {
        const category = dictionary[event].group;
        result[category] = [...(result[category] || []), event];
        return result;
      }, {}),
    [matches, dictionary]
  );
  const toggle = (event: string) =>
    setDraft((value) =>
      value.includes(event) ? value.filter((key) => key !== event) : [...value, event]
    );
  const select = (values: string[]) =>
    setDraft((value) => Array.from(new Set([...value, ...values])));
  const toggleGroup = (values: string[]) =>
    setDraft((value) =>
      values.every((event) => value.includes(event))
        ? value.filter((event) => !values.includes(event))
        : Array.from(new Set([...value, ...values]))
    );
  const highlight = (value: string) => {
    const term = search.trim();
    const index = value.toLowerCase().indexOf(term.toLowerCase());
    return !term || index < 0 ? (
      value
    ) : (
      <>
        {value.slice(0, index)}
        <mark>{value.slice(index, index + term.length)}</mark>
        {value.slice(index + term.length)}
      </>
    );
  };
  const keyboard = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape' && search) {
      event.stopPropagation();
      setSearch('');
    }
    if (event.key === '/' && (event.target as HTMLElement).tagName !== 'INPUT') {
      event.preventDefault();
      searchRef.current?.focus();
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      const inputs = Array.from(
        optionsRef.current?.querySelectorAll<HTMLInputElement>('input[type="checkbox"]') || []
      );
      const index = inputs.indexOf(document.activeElement as HTMLInputElement);
      if (inputs.length) {
        event.preventDefault();
        inputs[
          (index + (event.key === 'ArrowDown' ? 1 : -1) + inputs.length) % inputs.length
        ].focus();
      }
    }
    if (event.key === 'Enter' && (event.target as HTMLElement).tagName === 'INPUT') {
      event.preventDefault();
      onApply(draft);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      classes={{ paper: classes.paper }}
      aria-labelledby="activity-event-title"
      onKeyDown={keyboard}>
      <DialogTitle disableTypography className={classes.header}>
        <Box display="flex" alignItems="center">
          <Typography variant="h6" id="activity-event-title">
            Filter by event
          </Typography>
          <Button
            color="primary"
            onClick={() => {
              setDraft([]);
              setSearch('');
            }}>
            Reset
          </Button>
        </Box>
        <IconButton aria-label="Close event filter" onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          variant="outlined"
          margin="dense"
          className={classes.search}
          inputRef={searchRef}
          label="Search event types"
          placeholder="Try fraud, cognito, order…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <Box className={classes.selected}>
          <Typography variant="caption">Selected · {draft.length}</Typography>
          {draft.map((event) => (
            <Chip
              key={event}
              size="small"
              label={eventLabel(event, dictionary)}
              className={classes.chip}
              onDelete={() => toggle(event)}
            />
          ))}
          <Button size="small" color="primary" onClick={() => setDraft([])}>
            Clear all
          </Button>
        </Box>
        {search && (
          <Box className={classes.groupTitle}>
            <span>
              {matches.length} matches for “{search}” · matching name or key
            </span>
            <Button size="small" color="primary" onClick={() => select(matches)}>
              Select all {matches.length}
            </Button>
          </Box>
        )}
        <div ref={optionsRef}>
          {Object.keys(groups).map((name) => (
            <section key={name} className={classes.group}>
              <Box className={classes.groupTitle}>
                <strong style={{ color: categoryStyle(name).color }}>
                  {name} · {groups[name].length}
                </strong>
                <Button size="small" color="primary" onClick={() => toggleGroup(groups[name])}>
                  {groups[name].every((event) => draft.includes(event))
                    ? 'Deselect all'
                    : 'Select all'}
                </Button>
              </Box>
              {groups[name].map((event) => (
                <label key={event} className={classes.option}>
                  <Checkbox
                    color="primary"
                    checked={draft.includes(event)}
                    onChange={() => toggle(event)}
                  />
                  <span className={classes.name}>{highlight(eventLabel(event, dictionary))}</span>
                  <span className={classes.key}>{highlight(event.toLowerCase())}</span>
                </label>
              ))}
            </section>
          ))}
          {!matches.length && <Typography>No matching event types.</Typography>}
        </div>
      </DialogContent>
      <DialogActions className={classes.footer}>
        <Typography variant="caption">
          {draft.length
            ? `${draft.length} of ${Object.keys(dictionary).length} event types`
            : 'All event types, including other activity'}
        </Typography>
        <Box>
          <Button className={classes.button} color="primary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className={classes.button}
            color="primary"
            variant="contained"
            onClick={() => onApply(draft)}>
            Apply
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ActivityEventFilterDialog;
